import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Info, Menu, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useChrome } from '../context/ChromeContext';
import { useSidebar } from '../context/SidebarContext';
import { FavoriteButton } from './FavoriteButton';
import { archeInfoPath } from '@/lib/arche-url';

interface ArcheHeaderProps {
  archeId: string;
  title: string;
  children?: ReactNode;
  showFavorite?: boolean;
  /** Content rendered after the favorite star (e.g., FiasAI toggle) */
  leftActions?: ReactNode;
}

export function ArcheHeader({
  archeId,
  title,
  children,
  showFavorite = true,
  leftActions,
}: ArcheHeaderProps) {
  const { isCollapsed, toggle } = useSidebar();
  // ArcheHeader deliberately participates in platform chrome state: in this
  // arche's focus mode it removes itself (ArcheLayout drops the platform Header
  // too). State is per-arche, default visible — other arches are unaffected.
  const { isHeadersHidden, toggleHeaders } = useChrome();
  if (isHeadersHidden(archeId)) return null;

  return (
    <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
      {/* Wraps instead of overflowing: on a narrow window the action slot drops
          to its own row rather than running off the edge of the arche's scroll
          container, where it reads as clipped. The title truncates for the same
          reason — a long arche name must not push the icons out of reach. */}
      <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-center gap-2">
          {/* Sidebar toggle (always rendered to maintain layout, hidden when open) */}
          <Button
            variant="outline"
            size="icon"
            onClick={toggle}
            className={`h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
            title="Open sidebar"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <h1 className="truncate text-lg font-semibold">{title}</h1>
          <Link
            to={archeInfoPath(archeId)}
            className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors"
            title="View arche details"
          >
            <Info className="h-6 w-6" />
          </Link>
          {showFavorite && <FavoriteButton archeId={archeId} />}
          {/* Focus mode: hide all upper bars (this header + the platform Header). */}
          <button
            type="button"
            onClick={() => toggleHeaders(archeId)}
            className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors"
            title="Hide headers"
            aria-label="Hide headers"
          >
            <Maximize2 className="h-5 w-5" />
          </button>
          {leftActions}
        </div>
        {children && (
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">{children}</div>
        )}
      </div>
    </div>
  );
}

export default ArcheHeader;
